import { sortData, tryParse, isObjectEmpty } from "@/utils";
import { markRaw } from "vue";
export default class Filter {
  list;
  selected;
  // Set of item keys changed since the last apply(). select/deselect/clear
  // toggle membership here, so `draft.size` is the count of pending changes.
  // Only maintained when `staged` is true.
  draft;
  defaultList;
  shadowListCount = 0;
  modified = false;
  searching = false;
  keyword = "";
  searchCancelled = false;
  // Flag. When true (the default), select/deselect/clear/selectAll additionally
  // record the change in `draft` (for the pending-changes indicator). Items still
  // move between `list` and `selected` immediately either way. Pass
  // `{ staged: false }` in the constructor options to opt out.
  staged = true;
  constructor(list = [], selected = [], key = "id", save = false, name = "filter", asyncSearcher = null, autoSelect = true, options = {}) {
    // Edge case, for default selection when the list has only one item
    if (list.length === 1 && selected.length === 0 && autoSelect) {
      selected = list;
      list = [];
    }
    this.list = sortData(list, "__id");
    this.selected = sortData(selected ?? [], "__id");
    this.defaultList = sortData(list, "__id");
    this.staged = options.staged ?? true;
    // Tracks keys of items changed since the last apply().
    this.draft = new Set();
    this.key = key;
    this.save = save;
    this.name = name;
    this.asyncSearcher = asyncSearcher;
  }

  /**
   * Toggles an item's key in the `draft` change-set (staged mode only).
   * A key present means the item currently differs from the last-applied state,
   * so toggling on select/deselect keeps `draft.size` equal to the net number
   * of pending changes. O(1) per call.
   */
  _track(item, key = this.key) {
    if (!this.staged) return;
    const id = item[key];
    if (this.draft.has(id)) {
      this.draft.delete(id);
    } else {
      this.draft.add(id);
    }
  }

  /**
   * Bulk variant of `_track` for whole-list moves (selectAll/clear). Adds each
   * item's key to the draft set (staged mode only). Each add is O(1); we avoid
   * Set.union() because it isn't supported on Vue's reactive Set proxy.
   */
  _trackAll(items, key = this.key) {
    if (!this.staged) return;
    items.forEach(item => this.draft.add(item[key]));
  }

  /**
   *
   * @param {*} list
   * @param {*} selected
   * @param {*} key
   *
   * Remove selected from list based on the key passed. (deduplication)
   * @returns {any[]}
   */
  dedupSelected(list, selected, key) {
    // Remove selected from list based on the key passed.
    return list.filter(item => !selected.find(i => i[key] === item[key]));
  }

  /**
   *
   * @param {list: any[], selected: any[], key: string} param0
   *
   * Initialize the filter.
   */
  initialize(params = {}) {
    let {
      list = [],
      selected = [],
      save = false,
      name = "filter",
      asyncSearcher = null,
      autoSelect = false
    } = params;
    // Edge case, for default selection when the list has only one item
    if (list.length === 1 && selected.length === 0 && autoSelect) {
      selected = list;
      list = [];
    }
    this.list = sortData(list, "__id");
    this.selected = sortData(selected, "__id");
    this.defaultList = sortData(list, "__id");
    if (params.key) {
      this.key = params.key;
    }
    // Reset the pending-change tracker on (re)initialization.
    this.draft = new Set();
    this.save = save;
    this.name = name;
    this.asyncSearcher = asyncSearcher;
    this.apply();
  }

  /**
   *
   * @param {any} item
   * @param {string} key
   *
   * Select an item from the list and move it to the selected list.
   *
   * @returns {void}
   */
  static isMatch(a, b, caseSensitive = false) {
    if (Array.isArray(a) && Array.isArray(b)) {
      return a.length === b.length && a.every((val, index) => val === b[index]);
    }
    const aStr = String(a);
    const bStr = String(b);
    if (caseSensitive) {
      return aStr === bStr;
    }
    return aStr.toLowerCase() === bStr.toLowerCase();
  }
  static removeMatchingItem(list, item, key, caseSensitive = false) {
    return sortData(list.filter(i => !Filter.isMatch(i[key], item[key], caseSensitive)), "__id");
  }
  select(item, key, options = {}) {
    key = this._getFilterKey(key);
    const caseSensitive = options.case_sensitive || false;
    if (item[key] === null || item[key] === undefined) {
      console.error('Item not found', item, key);
      return;
    }
    ;

    // Find the item in the list based on the key
    const foundItem = this.list.find(i => Filter.isMatch(i[key], item[key], caseSensitive));
    if (!foundItem) {
      console.error('Item not found', key);
      return;
    }
    ;

    // Remove the found item from the list and add it to the selection if not
    // already present, recording the change in the draft set.
    this.list = Filter.removeMatchingItem(this.list, item, key, caseSensitive);
    if (!this.selected.some(i => Filter.isMatch(i[key], item[key], caseSensitive))) {
      this.selected = sortData([...this.selected, foundItem], "__id");
      this._track(foundItem, key);
    }
    this.modified = true;
    this.saveFilter();
  }
  selectMultiple(items, key, options = {}) {
    for (const item of items) {
      this.select(item, key, options);
    }
  }

  /**
   * Select all items from the list and move it to the selected list.
   */
  selectAll() {
    this._trackAll(this.list);
    this.selected = sortData([...this.selected, ...this.list], "__id");
    this.list = [];
    this.modified = true;
    this.saveFilter();
  }

  /**
   *
   * @param {string} value
   */
  set _key(value) {
    this.key = value;
  }

  /**
   *
   * @param {any} item
   * @param {string} key
   */
  selectOnly(item, key, options = {}) {
    // This is like doing a `clear` and after that `select`
    this.clear();
    this.select(item, key, options);
  }

  /**
   *
   * @param {any} item
   * @param {string} key
   */
  selectWhenNoSelected(item, key, options = {}) {
    // This is like doing a `clear` and after that `select` if there is no selected
    if (this.selected.length == 0) {
      this.selectOnly(item, key, options);
    }
  }

  /**
   *
   * @param {any} item
   * @param {string} key
   */
  selectOneWhenMultipleSelected(item, key, options = {}) {
    // This is like doing a `clear` and after that `select` if there is no selected
    if (this.selected.length > 1) {
      this.selectOnly(item, key, options);
    }
  }
  _getFilterKey(key) {
    return key ?? this.key;
  }

  /**
   *
   * @param {any} item
   * @param {string} key
   *
   * Deselect an item from the selected list and move it to the list.
   */

  deselect(item, key, options = {}) {
    key = this._getFilterKey(key);
    const caseSensitive = options.case_sensitive || false;
    if (item[key] === null || item[key] === undefined) {
      console.error('key not found');
      return;
    }
    ;
    // Find the item in the committed selection.
    const foundItem = this.selected.find(i => Filter.isMatch(i[key], item[key], caseSensitive));
    if (!foundItem) {
      console.error('Item not found');
      return;
    }
    ;

    // Remove item from the selection and record the change in the draft set.
    this.selected = Filter.removeMatchingItem(this.selected, item, key, caseSensitive);
    this._track(foundItem, key);

    // Add item to list
    this.list = sortData([...this.list, foundItem], "__id");
    this.modified = true;
    this.saveFilter();
  }

  /**
   * Clears the selected list and moves all elements from selected to list.
   */
  clear() {
    // Record each removed item, then move the whole selection back to the list.
    this._trackAll(this.selected);
    this.list = sortData([...this.list, ...this.selected], "__id");
    this.selected = [];

    // Mark filter as modified
    this.modified = true;
    this.saveFilter();
  }

  /**
   * Clears the filter and immediately applies the changes to avoid inconsistent state.
   * Use when clearing dependent filters after parent filter changes.
   */
  clearAndApply() {
    this.clear();
    this.apply();
  }

  /**
   * Discards the pending changes, reverting the selection to its last-applied
   * state. Backs the "Discard" button and the dropdown-closed-without-applying
   * case. Each tracked key is swapped back to the bucket it came from: items
   * removed this session return to `selected`, items added this session drop
   * out of it. No-op when not staged.
   */
  clearDraft() {
    if (!this.staged) return;

    // Nothing moved this session -> nothing to revert.
    if (this.draft.size === 0) return;

    // Items removed this session (now in the list, and tracked) go back to the
    // selection; items added this session are dropped and rebuilt into the list.
    const restored = this.list.filter(i => this.draft.has(i[this.key]));
    this.selected = sortData([...this.selected.filter(i => !this.draft.has(i[this.key])), ...restored], "__id");
    this.list = sortData(this.dedupSelected(this.defaultList, this.selected, this.key), "__id");
    this.draft.clear();
    this.keyword = "";
    this.searching = false;
    this.shadowListCount = 0;
    this.modified = false;
    this.saveFilter();
  }

  /**
   * Restores the list with the defaultList, excluding selected items.
   * Used when search is cleared. Cancels any in-flight async search.
   * @param {string} key - Optional key parameter, defaults to this.key
   */
  restoreSearch(key) {
    this.searchCancelled = true;
    this.keyword = "";
    this.shadowListCount = 0;
    key = key ?? this.key;
    this.searching = false;
    this.list = sortData(this.dedupSelected(this.defaultList, this.selected, key), "__id");
  }

  /**
  * Performs and `empty` search to load `original` list from backend and
  * clears the selected list and moves all elements from selected to list.
  */
  async clearAfterAsyncSearch({
    callback,
    args
  }) {
    this.clear();
    this.asyncSearch({
      callback,
      keyword: [],
      args
    });
    this.apply();
  }

  /**
   * @param {any[]} data
   * Clears the selected list and moves all elements from selected to list.
   */
  set list(data) {
    // Move all elements from selected to list
    this.list = sortData(data, "__id");
  }

  /**
   *
   * @param {string} keyword
   * @param {string | string[] | undefined} key
   */
  listSearch(keyword, key) {
    // keyword can be a string or an array of strings
    this.keyword = keyword;
    this.searching = true;
    if (!Array.isArray(keyword)) {
      keyword = [keyword];
    }

    // Map all elements in keyword list into lowercase
    const keywords = keyword.map(item => item.toLowerCase()).map(item => {
      // Regex split based on [space, comma, semicolon]
      return item.split(/[\s,;]+/);
    }).flat();
    // Further break down the keyword into individual words

    // Filter list based on keyword
    key = key ?? this.key;
    if (key && !Array.isArray(key)) {
      key = [key];
    }
    if (keywords.length === 0) {
      this.keyword = "";
      this.searching = false;
      this.list = sortData(this.dedupSelected(this.defaultList, this.selected, key[0] ?? this.key), "__id");
      return;
    }
    const lowerCaseKeywords = keywords.map(k => k.toLowerCase());
    const results = this.defaultList.filter(item => {
      return key.some(k => {
        const searchSpace = item[k];
        if (searchSpace) {
          // If searchSpace is an array, convert all elements to lowercase and check for any match
          if (Array.isArray(searchSpace)) {
            return searchSpace.some(val => lowerCaseKeywords.some(keyword => val.toString().toLowerCase().includes(keyword)));
          }
          // If it's a string, convert to lowercase and check for a match
          return lowerCaseKeywords.some(keyword => searchSpace.toString().toLowerCase().includes(keyword));
        }
        return false;
      });
    });

    // Remove selected from list based on the key passed.
    this.list = Array.from(new Set(sortData(this.dedupSelected(results, this.selected, key[0] ?? this.key), "__id")));
  }
  /**
   * Applies the current selection. Since edits are made live, this just clears
   * the pending-change tracker and the modified flag.
   */
  apply() {
    this.draft.clear();
    this.modified = false;
    this.saveFilter();
  }

  /**
   *
   * @returns {boolean}
   */
  get isModified() {
    return this.modified;
  }
  /**
   *
   * @param {string} keyword
   * @param {Function} callback
   */
  async asyncSearch({
    callback,
    keyword,
    args,
    key
  }) {
    if (!callback) {
      callback = this.asyncSearcher;
    }
    if (!callback) {
      throw new Error("No async searcher defined");
    }
    if (!key) {
      key = this.key;
      //console.log('key', key);
    }
    this.keyword = keyword;
    this.searchCancelled = false;
    this.searching = true;
    if (keyword?.length === 0) {
      this.keyword = "";
      this.searching = false;
      this.shadowListCount = 0;
      this.list = sortData(this.dedupSelected(this.defaultList, this.selected, key), "__id");
      return this.list;
    }
    // Filter list based on keyword

    try {
      let results = await callback({
        keyword,
        ...args
      });

      // If search was cancelled, don't update results
      if (this.searchCancelled) {
        this.searching = false;
        this.list = this.defaultList;
        return;
      }

      //console.log('before results', results)

      // Check if results is an error response
      if (results && (results.error || results.status >= 400)) {
        this.searching = false;
        this.shadowListCount = 0;
        return this.list;
      }

      // Remove any excess that doesn't match the keyword based on the passed key
      // results = this.filterResultsByKeyword(results, key, keyword);

      //console.log('after results', results)
      const dedupedResults = this.dedupSelected(results, this.selected, key);
      this.shadowListCount = dedupedResults.length;
      this.list = sortData(dedupedResults, "__id");
      this.searching = false;
    } catch (error) {
      // Ignore abort errors
      if (error.name !== 'AbortError') {
        console.error("Error during async search:", error);
      }
      this.searching = false;
    }
  }

  /**
   * Returns the appropriate list to display based on search state.
   * If no search is active (keyword is empty), shows defaultList.
   * Otherwise shows the current filtered/search results list.
   * @returns {any[]}
   */
  get displayList() {
    return this.list;
  }

  /**
   * Reports pending (staged) changes for UI feedback.
   * Cleared everything -> "RESET TO ALL" string (caller appends the filter name).
   * Otherwise -> { count, text: "CHANGES RECORDED" }. Null when nothing pending.
   * @returns {{count: number, text: string} | string | null}
   */
  get pendingChanges() {
    if (!this.staged || this.draft.size === 0) return null;

    // Everything was cleared out of the selection.
    if (this.selected.length === 0) return "RESET TO ALL";
    const count = this.draft.size;
    return {
      count,
      text: `${count === 1 ? "CHANGE" : "CHANGES"} RECORDED`
    };
  }

  /**
   * Indicates if we should show the "searching" UI state.
   * True when there are results in the shadow list (async search has returned results).
   * @returns {boolean}
   */
  get isSearching() {
    return this.shadowListCount > 0;
  }

  /**
   * @returns {Object{list: any[], selected: any[]}}
   *  */
  get filterState() {
    return {
      list: this.list,
      selected: this.selected,
      selectedCount: this.selected.length
    };
  }

  /**
   * @returns {any[]}
   *
   */
  get filterValues() {
    // Return list when selected is empty otherwise return selected
    const res = markRaw(this.selected.length > 0 ? this.selected : this.list);
    return res.map(item => item[this.key]);
  }
  get filterItems() {
    // Return list when selected is empty otherwise return selected without dereferencing
    const res = markRaw(this.selected.length > 0 ? this.selected : this.list);
    return res;
  }
  get selectedItems() {
    // Return  the selected items in a list as is
    const res = markRaw(this.selected);
    return res;
  }
  get selectedValues() {
    return this.selected.map(item => item[this.key]);
  }

  /**
   * Returns a JSON string that can be saveable.
   * @returns {string}
   */
  serialize() {
    return JSON.stringify(this);
  }

  /**
   * Standard entry point for applying a restored snapshot (JSON string, plain
   * object, or Filter) onto this instance in place. Resets transient runtime
   * state and keeps this instance's asyncSearcher.
   * @param {string|object|Filter} source
   * @returns {Filter} this
   */
  restore(source) {
    const data = typeof source === "string" ? tryParse(source) : source;
    if (!data || isObjectEmpty(data)) return this;
    this.list = sortData(data.list ?? [], "__id");
    this.selected = sortData(data.selected ?? [], "__id");
    this.defaultList = sortData(data.defaultList ?? data.list ?? [], "__id");
    this.key = data.key ?? this.key;
    this.name = data.name ?? this.name;
    this.save = data.save ?? this.save;
    this.staged = data.staged ?? true;
    this.draft = new Set();
    this.keyword = "";
    this.searching = false;
    this.searchCancelled = false;
    this.shadowListCount = 0;
    this.modified = true;
    return this;
  }

  /**
   * Restore a filter from a JSON string into a new instance.
   */
  static deserialize(serialized) {
    return new Filter([], []).restore(serialized);
  }

  /**
   * Saves the filter to local storage.
   */
  saveFilter() {
    let current_workspace = tryParse(localStorage.getItem("current_workspace"));
    if (!isObjectEmpty(current_workspace)) {
      localStorage.setItem(current_workspace?.workspace_id + this.name, this.serialize());
    }
  }

  /**
   *
   * @param {callback, keyword, args} param0
   * @param {Object} item
   * @param {string} id
   * @returns boolean
   */
  async asyncSearchAndSelect({
    callback,
    keyword,
    args,
    key
  }) {
    await this.asyncSearch({
      callback,
      keyword,
      args,
      key
    });
    this.searching = false;
    if (this.list.length === 0) {
      return false;
    }
    this.selectAll(); // use select All to capture all elements returned by the async search
    return true;
  }

  /**
   * Clone the current filter.
   * @returns {Filter}
   */
  clone() {
    return Filter.deserialize(this.serialize());
  }

  /**
   * This is a deep comparison between two filters.
   * @param {Filter} filter
   */
  isEqual(filter) {
    if (!(filter instanceof Filter)) {
      return false;
    }

    // Compare each property individually [list, selected] te important ones
    return JSON.stringify(this.list) === JSON.stringify(filter.list) && JSON.stringify(this.selected) === JSON.stringify(filter.selected);
  }

  /**
   * Checks whether the `selected list` is empty or not.
   * Returns false if not empty and true when empty.
   * @returns {boolean}
   */
  get isEmpty() {
    return this.selected.length === 0;
  }
  filterResultsByKeyword(results, key, keywordArray) {
    const keywordSet = new Set(keywordArray);
    return results.filter(el => {
      if (!(key in el)) return true;
      const value = el[key];
      const valuesToCheck = Array.isArray(value) ? value : [value];
      return valuesToCheck.some(v => keywordSet.has(v));
    });
  }

  /**
   * Flatten an array field from items
   * @param {string} fieldName - Field to extract
   * @param {array} items - Source items
   * @returns {array} Flattened, filtered array
   */
  static flatten(fieldName, items) {
    return items.flatMap(item => item[fieldName] || []).flat().filter(Boolean);
  }

  /**
   * Merge array fields from two sources (focused selection pattern)
   * Prefers source2 (focused/current selection) when it has values
   * Falls back to source1, then empty
   * Automatically flattens nested arrays
   *
   * @param {string|object} fieldMap - Field name(s):
   *   - string: same field name for both sources
   *   - object: { source1: 'field1', source2: 'field2' }
   * @param {array} source1 - Fallback source (used if source2 is empty)
   * @param {array} source2 - Preferred source (focused/current selection)
   * @returns {array} Flattened, filtered array
   */
  static merge(fieldMap, source1, source2) {
    const getFieldName = key => typeof fieldMap === 'string' ? fieldMap : fieldMap[key];
    const extract = (items, fieldName) => items.flatMap(item => item[fieldName] || []).flat().filter(Boolean);
    return source2.length ? extract(source2, getFieldName('source2')) : extract(source1, getFieldName('source1'));
  }
}
export class RefreshableFilter {
  constructor(storeFilter, initializer, name = "", rootPath = "") {
    this.storeFilter = storeFilter;
    this.initializer = initializer;
    this.name = name || storeFilter.name || "unnamed_filter";
    this.rootPath = rootPath;
  }
  async refresh(context = {}) {
    if (!this.initializer) return;
    // Pass exactly the three arguments expected by initialize*Filters
    console.log('calling intializer', context, this.storeFilter, this.rootPath);
    await this.initializer(context, this.storeFilter, this.rootPath);
  }
}

// utils/filters/FilterManager.js
export class FilterManager {
  /**
   * @param {Object} options
   * @param {Filter} options.sellersFilter
   * @param {Filter} options.marketplacesFilter
   * @param {string} options.rootPath - common root path for all filters
   */
  constructor({
    sellersFilter,
    marketplacesFilter,
    rootPath = ""
  } = {}) {
    this.filters = [];
    this.sellersFilter = sellersFilter;
    this.marketplacesFilter = marketplacesFilter;
    this.rootPath = rootPath;
  }
  register(refreshableFilter) {
    this.filters.push(refreshableFilter);
  }
  get defaultProps() {
    return {
      sellers: this.sellersFilter?.filterValues,
      sales_channel: this.marketplacesFilter?.filterValues
    };
  }
  registerAll(filterMap) {
    for (const [name, {
      storeFilter,
      initializer
    }] of Object.entries(filterMap)) {
      this.register(new RefreshableFilter(storeFilter, initializer, name, this.rootPath));
    }
  }
  async refreshAll(extraParams = {}) {
    const context = {
      ...this.defaultProps,
      ...extraParams
    };
    return Promise.all(this.filters.map(f => f.refresh(context)));
  }
  refreshAllBackground(extraParams = {}) {
    const context = {
      ...this.defaultProps,
      ...extraParams
    };
    this.filters.forEach(f => f.refresh(context).catch(console.error));
  }
  async refreshOne(name, extraParams = {}) {
    const filter = this.filters.find(f => f.name === name);
    if (!filter) return;
    const context = {
      ...this.defaultProps,
      ...extraParams
    };
    await filter.refresh(context);
  }
  async refreshSet(filterKeys = [], extraParams = {}) {
    const context = {
      ...this.defaultProps,
      ...extraParams
    };
    return Promise.all(filterKeys.map(async name => {
      const filter = this.filters.find(f => f.name === name);
      if (!filter) return;
      await filter.refresh(context);
    }));
  }
}